import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { Analytics } from '@/lib/analytics';

export interface SleepLog {
  id: string;
  sleep_date: string;
  hours_slept: number;
  quality_rating: number | null;
  notes: string | null;
}

export const useSleepTracking = () => {
  const { user } = useAuth();
  const [sleepLogs, setSleepLogs] = useState<SleepLog[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchSleepLogs = async (days = 30) => {
    if (!user) return;
    
    setLoading(true);
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      
      const { data, error } = await supabase
        .from('sleep_logs')
        .select('*')
        .eq('user_id', user.id)
        .gte('sleep_date', startDate.toISOString().split('T')[0])
        .order('sleep_date', { ascending: false });

      if (error) throw error;
      setSleepLogs(data || []);
    } catch (error) {
      console.error('Error fetching sleep logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const logSleep = async (sleepData: Omit<SleepLog, 'id'>) => {
    if (!user) return;

    try {
      console.log('Logging sleep data:', sleepData);
      
      const { data, error } = await supabase
        .from('sleep_logs')
        .upsert([{
          ...sleepData,
          user_id: user.id
        }], {
          onConflict: 'user_id,sleep_date',
          ignoreDuplicates: false
        })
        .select()
        .single();

      if (error) {
        console.error('Error logging sleep:', error);
        throw error;
      }
      
      console.log('Sleep logged successfully:', data);
      Analytics.sleepLogged(sleepData.hours_slept, sleepData.quality_rating || 5);
      await fetchSleepLogs();
      return data;
    } catch (error) {
      console.error('Error logging sleep:', error);
      throw error;
    }
  };

  const getWeeklyAverage = () => {
    if (sleepLogs.length === 0) return 0;
    
    const last7Days = sleepLogs.slice(0, 7);
    const total = last7Days.reduce((sum, log) => sum + log.hours_slept, 0);
    return Math.round((total / last7Days.length) * 10) / 10;
  };

  const getSleepTrend = () => {
    if (sleepLogs.length < 14) return 'stable';
    
    const recent7Days = sleepLogs.slice(0, 7);
    const previous7Days = sleepLogs.slice(7, 14);
    
    const recentAvg = recent7Days.reduce((sum, log) => sum + log.hours_slept, 0) / recent7Days.length;
    const previousAvg = previous7Days.reduce((sum, log) => sum + log.hours_slept, 0) / previous7Days.length;
    
    if (recentAvg > previousAvg + 0.5) return 'improving';
    if (recentAvg < previousAvg - 0.5) return 'declining';
    return 'stable';
  };

  useEffect(() => {
    if (user) {
      fetchSleepLogs();
    }
  }, [user]);

  return {
    sleepLogs,
    loading,
    logSleep,
    getWeeklyAverage,
    getSleepTrend,
    refetch: fetchSleepLogs
  };
};